import React, { useEffect, useId, useState } from 'react';
import { getSubdivisions, REPORT_COUNTRIES, reportAreaLabel, type ReportArea } from '../../functions/src/common/dailyReportArea';

interface Props {
    area: ReportArea;
    loading: boolean;
    canSave: boolean;
    error?: string;
    onRetry?: () => void;
    onCountryChange: (country: string) => void;
    onSave: (area: ReportArea) => Promise<void>;
}

const styles = {
    panel: 'shrink-0 border-b border-slate-200 bg-slate-50 px-6 py-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100',
    input: 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100',
    button: 'rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50',
    muted: 'text-xs text-slate-500 dark:text-slate-400',
};

export default function ReportAreaPicker({ area, loading, canSave, error, onRetry, onCountryChange, onSave }: Props) {
    const id = useId();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string[]>(area.subdivisionCodes);
    const [mode, setMode] = useState(area.subdivisionCodes.length ? 'states' : 'country');
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savedCodes = area.subdivisionCodes.join(',');
    useEffect(() => {
        setDraft(savedCodes ? savedCodes.split(',') : []);
        setMode(savedCodes ? 'states' : 'country');
    }, [area.country, savedCodes]);
    const regions = getSubdivisions(area.country);
    const matches = regions.filter(region => region.name.toLowerCase().includes(search.toLowerCase()));
    const save = async () => {
        setSaving(true);
        setSaveError('');
        try {
            await onSave({ country: area.country, subdivisionCodes: mode === 'states' ? draft : [] });
            setEditing(false);
        } catch {
            setSaveError('Could not confirm this area was saved. Please retry.');
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className={styles.panel}>
            <div className="flex flex-wrap items-center gap-3">
                <label htmlFor={`${id}-country`} className="font-medium">Report area</label>
                <select id={`${id}-country`} className={styles.input} value={area.country} disabled={saving} onChange={event => onCountryChange(event.target.value)}>
                    <option value="">Choose country</option>
                    {area.country && !REPORT_COUNTRIES.some(country => country.code === area.country) && <option value={area.country}>{area.country}</option>}
                    {REPORT_COUNTRIES.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
                </select>
                {area.country && <span className="order-last w-full text-xs" aria-live="polite">{loading ? 'Loading saved area…' : reportAreaLabel(area)}</span>}
                {area.country && <button type="button" className={styles.button} disabled={loading || !canSave || saving || !!error} aria-expanded={editing} aria-controls={`${id}-editor`} onClick={() => {
                    setDraft([...area.subdivisionCodes]);
                    setMode(area.subdivisionCodes.length ? 'states' : 'country');
                    setSaveError('');
                    setEditing(!editing);
                }}>Change area</button>}
            </div>
            {error && <p role="alert" className="mt-2 text-red-600 dark:text-red-400">{error}</p>}
            {error && onRetry && <button type="button" className={`${styles.input} mt-2`} onClick={onRetry}>Retry loading area</button>}
            {editing && (
                <div id={`${id}-editor`} className="mt-3 space-y-3">
                    <fieldset disabled={saving} className="flex flex-wrap gap-4">
                        <legend className="mb-2 font-medium">Daily Micro coverage</legend>
                        <label className="flex items-center gap-2"><input type="radio" name={`${id}-mode`} checked={mode === 'country'} onChange={() => setMode('country')} />Whole country</label>
                        <label className="flex items-center gap-2"><input type="radio" name={`${id}-mode`} checked={mode === 'states'} disabled={!regions.length} onChange={() => setMode('states')} />States / provinces</label>
                    </fieldset>
                    {!regions.length && <p className={styles.muted}>State selection currently supports the United States, Canada, and Australia.</p>}
                    {mode === 'states' && <>
                        <label className="block" htmlFor={`${id}-search`}>Find a state or province</label>
                        <input id={`${id}-search`} className={`${styles.input} w-full`} value={search} disabled={saving} onChange={event => setSearch(event.target.value)} type="search" />
                        <fieldset disabled={saving} className="grid max-h-40 grid-cols-1 gap-2 overflow-auto sm:grid-cols-2">
                            <legend className={styles.muted}>{draft.length} selected</legend>
                            {matches.map(region => <label key={region.code} className="flex items-center gap-2 py-1"><input type="checkbox" checked={draft.includes(region.code)} onChange={event => setDraft(event.target.checked ? [...draft, region.code] : draft.filter(code => code !== region.code))} />{region.name}</label>)}
                        </fieldset>
                        {!matches.length && <p className={styles.muted}>No matching states or provinces.</p>}
                    </>}
                    <p className={styles.muted}>Saved for your Daily Micro reports in this country. Regional reports refresh daily at 07:00 UTC. Select other countries on the map.</p>
                    {saveError && <p role="alert" className="text-red-600 dark:text-red-400">{saveError}</p>}
                    <div className="flex gap-3">
                        <button type="button" className={styles.button} disabled={saving || mode === 'states' && !draft.length} onClick={save}>{saving ? 'Saving and preparing report…' : 'Save report area'}</button>
                        <button type="button" disabled={saving} className={styles.input} onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}
